import React from 'react';
import { Link } from 'react-router-dom';

function Transparency() {
  return (
    <div className="doc-page">
      {/* Navigation */}
      <nav className="nav">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <span className="logo-text">AVALW</span>
            <span className="logo-badge">Worker</span>
          </Link>
          <div className="nav-links">
            <Link to="/transparency" className="nav-link">Transparency</Link>
            <Link to="/protocol" className="nav-link">Protocol</Link>
            <Link to="/security" className="nav-link">Security</Link>
            <Link to="/dashboard" className="nav-btn">Dashboard</Link>
          </div>
        </div>
      </nav>

      <div className="doc-container">
        <div className="doc-header">
          <h1 className="doc-title">Transparency</h1>
          <p className="doc-subtitle">
            Complete documentation of what the AVALW Worker does, what data it sends, and how it works.
          </p>
        </div>

        {/* What Worker Does */}
        <section className="doc-section">
          <h2>What The Worker Does</h2>

          <div className="info-box success">
            <div className="info-box-title">Distributed Search Network</div>
            <p>The worker contributes to a faster, more private search network. By running the worker, you help speed up the AVALW network while also benefiting from faster search performance yourself.</p>
          </div>

          <h3>Key Benefits</h3>
          <ul>
            <li><strong>Faster Network</strong> - Your worker helps distribute the load, making searches faster for everyone</li>
            <li><strong>Privacy First</strong> - Search queries are processed privately without tracking</li>
            <li><strong>Mutual Benefit</strong> - You contribute compute power, you get faster searches and API access</li>
            <li><strong>Decentralized</strong> - No single point of failure, more resilient network</li>
          </ul>
        </section>

        {/* What Worker Does NOT Do */}
        <section className="doc-section">
          <h2>What The Worker Does NOT Do</h2>

          <div className="info-box danger">
            <div className="info-box-title">NOT Cryptocurrency Mining</div>
            <p>Zero cryptocurrency operations. No blockchain. No hashing for coins. The worker does NOT mine Bitcoin, Ethereum, or any other cryptocurrency.</p>
          </div>

          <h3>Also NOT:</h3>
          <ul>
            <li><strong>Proxy/VPN</strong> - Your network is never used as a proxy</li>
            <li><strong>Botnet Activity</strong> - No DDoS, spam, or malicious traffic</li>
            <li><strong>File Sharing</strong> - No torrents or illegal content</li>
            <li><strong>Malware</strong> - No viruses, trojans, or spyware</li>
            <li><strong>Data Harvesting</strong> - No scraping of your personal files</li>
          </ul>
        </section>

        {/* CPU Usage */}
        <section className="doc-section">
          <h2>CPU Usage Limits</h2>
          <p>The worker is designed to use minimal resources and not interfere with your work.</p>

          <table className="doc-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Core-Hours</th>
                <th>Target CPU</th>
                <th>Requests/Day</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Starter</td>
                <td>3 CH</td>
                <td>~30%</td>
                <td>1,000</td>
              </tr>
              <tr>
                <td>Basic</td>
                <td>12 CH</td>
                <td>~40%</td>
                <td>5,000</td>
              </tr>
              <tr>
                <td>Pro</td>
                <td>36 CH</td>
                <td>~50%</td>
                <td>20,000</td>
              </tr>
              <tr>
                <td>Business</td>
                <td>72 CH</td>
                <td>~50%</td>
                <td>50,000</td>
              </tr>
              <tr>
                <td>Enterprise</td>
                <td>240 CH</td>
                <td>~50%</td>
                <td>500,000</td>
              </tr>
            </tbody>
          </table>

          <div className="info-box info">
            <div className="info-box-title">Formula</div>
            <p><code>Core-Hours = Cores x (Speed / 3.0 GHz) x Usage% x Hours</code></p>
          </div>
        </section>

        {/* Data Sent */}
        <section className="doc-section">
          <h2>Data Sent to Server</h2>
          <p>Here is exactly what data the worker sends during heartbeats:</p>

          <table className="doc-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Example</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Worker Token</td>
                <td><code>abc123...xyz</code></td>
                <td>Authentication</td>
              </tr>
              <tr>
                <td>CPU Cores</td>
                <td><code>8</code></td>
                <td>Tier calculation</td>
              </tr>
              <tr>
                <td>CPU Speed</td>
                <td><code>3.6 GHz</code></td>
                <td>Speed factor</td>
              </tr>
              <tr>
                <td>CPU Model</td>
                <td><code>Intel i7-12700K</code></td>
                <td>Display only</td>
              </tr>
              <tr>
                <td>Platform</td>
                <td><code>win32</code></td>
                <td>Compatibility</td>
              </tr>
              <tr>
                <td>Session Time</td>
                <td><code>2.5 hours</code></td>
                <td>Core-Hours calc</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Data NOT Sent */}
        <section className="doc-section">
          <h2>Data NOT Collected</h2>

          <div className="info-box success">
            <div className="info-box-title">Privacy Protected</div>
            <p>We do NOT collect: IP addresses, MAC addresses, location, browsing history, files, passwords, personal documents, or any identifying information.</p>
          </div>
        </section>

        {/* Kill Switch */}
        <section className="doc-section">
          <h2>Kill Switch - How to Stop</h2>
          <p>You have complete control. Stop the worker instantly:</p>

          <h3>Method 1: Keyboard</h3>
          <div className="code-block">
            <div className="code-header">Terminal</div>
            <pre>Press Ctrl+C in the terminal window</pre>
          </div>

          <h3>Method 2: Task Manager</h3>
          <ul>
            <li>Windows: Task Manager → End "node.exe" process</li>
            <li>Mac: Activity Monitor → Quit "node" process</li>
            <li>Linux: <code>pkill -f avalw-worker</code></li>
          </ul>

          <h3>Method 3: Uninstall</h3>
          <div className="code-block">
            <div className="code-header">Terminal</div>
            <pre>npm uninstall -g @avalw/search-worker</pre>
          </div>
        </section>

        {/* Back Link */}
        <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e2e8f0' }}>
          <Link to="/" className="btn btn-outline">Back to Home</Link>
        </div>
      </div>
    </div>
  );
}

export default Transparency;
